import { gold, green, grey, purple, red } from '@ant-design/colors';
import type { RouterOutputs } from '@client/trpc/client';
import type { ControlStatus, ControlTestOutcome } from '@shared/constants';
import { addDays, isPast, isWithinInterval } from '@shared/utils';

type DueDateStatus = {
    color: string;
    title: string;
};

type ControlTests = RouterOutputs['control']['test']['getControlTest'];

export function getControlTestStatus(
    dueDate: Date,
    outcome?: ControlTestOutcome,
    status?: ControlStatus,
): DueDateStatus {
    if (status === 'DRAFT') {
        return { color: grey[0], title: 'Draft' };
    }

    if (outcome === 'FAIL') {
        return { color: purple[3], title: 'Failed' };
    }

    const now = new Date();

    if (isPast(dueDate)) {
        return { color: red[5], title: 'Overdue' };
    }

    const closeDateThreshold = addDays(now, 3);
    if (isWithinInterval(dueDate, { start: now, end: closeDateThreshold })) {
        return { color: gold[5], title: 'Due date approaching' };
    }

    return { color: green[6], title: 'Not yet due' };
}

export function getControlActivityStatus(
    dueDate?: Date | null,
    dateCompleted?: Date | null,
    status?: ControlStatus,
): DueDateStatus {
    if (status === 'DRAFT') {
        return { color: grey[0], title: 'Draft' };
    }

    // If completed, show as completed
    if (dateCompleted) {
        return { color: green[6], title: 'Completed' };
    }

    // If no due date set, show as pending
    if (!dueDate) {
        return { color: grey[3], title: 'No due date set' };
    }

    const now = new Date();

    if (isPast(dueDate)) {
        return { color: red[5], title: 'Overdue' };
    }

    const closeDateThreshold = addDays(now, 3);
    if (isWithinInterval(dueDate, { start: now, end: closeDateThreshold })) {
        return { color: gold[5], title: 'Due date approaching' };
    }

    return { color: green[6], title: 'Not yet due' };
}

export function getHighestPriorityStatus(
    status: ControlStatus,
    tests: Pick<ControlTests, 'outcome' | 'nextTestDue' | 'testDate'>[],
) {
    if (status === 'DRAFT') {
        return { color: grey[0], title: 'Draft' };
    }

    if (tests.some((test) => test.outcome === 'FAIL')) {
        return { color: purple[3], title: 'Failed' };
    }

    const incompleteTests = tests.filter((test) => !test.testDate && !test.outcome);
    if (incompleteTests.some((test) => isPast(test.nextTestDue))) {
        return { color: red[5], title: 'Overdue' };
    }

    const now = new Date();
    const closeDateThreshold = addDays(now, 3);
    if (
        incompleteTests.some((test) =>
            isWithinInterval(test.nextTestDue, {
                start: now,
                end: closeDateThreshold,
            }),
        )
    ) {
        return { color: gold[5], title: 'Due date approaching' };
    }

    return { color: green[6], title: 'Not yet due' };
}
